import { CountryCode } from 'libphonenumber-js';

export const countUnreadIndeedMessagesPayload = {
  operationName: 'UnreadConversationCount',
  variables: {
    input: {
      conversationFilter: {
        contexts: ['RESUME_CONTACT'],
      },
      maxCountLimit: 100,
    },
  },
  extensions: {},
  query:
    'query UnreadConversationCount($input: GetUnreadConversationCountInput!) {\n  getUnreadConversationCount(input: $input) {\n    unreadConversationCount\n    __typename\n  }\n}\n',
};

export const subscriptionInfoPayload = {
  operationName: 'SubscriptionInfo',
  variables: {
    onlyCurrentUser: true,
    excludeSharedSubscriptions: false,
    subscriptionStatusFilter: 'ALL',
    mergeByAction: true,
  },
  query:
    'query SubscriptionInfo($onlyCurrentUser: Boolean = true, $excludeSharedSubscriptions: Boolean = false, $subscriptionStatusFilter: SubscriptionStatusFilter = ALL, $mergeByAction: Boolean = true) {\n  currentEmployerUser {\n    account {\n      accountProfile {\n        email {\n          emailAddress\n        }\n      }\n    }\n  }\n  talentSubscriptions(input: {onlyCurrentUser: $onlyCurrentUser, excludeSharedSubscriptions: $excludeSharedSubscriptions, subscriptionStatusFilter: $subscriptionStatusFilter}, first: 10) {\n    subscriptions {\n      status\n    }\n  }\n  talentEntitlements(input: {onlyCurrentUser: $onlyCurrentUser, mergeByAction: $mergeByAction, excludeSharedSubscriptions: $excludeSharedSubscriptions}, first: 200) {\n    entitlements {\n      action\n      quantity\n    }\n  }\n}\n',
};

export const getNewIndeedMessagesPayload = (take: number) => {
  return {
    extensions: {},
    operationName: 'FindConversationsByContext',
    query:
      'query FindConversationsByContext($contexts: [ConversationContext!], $scope: [ConversationScopeInput!], $before: String, $after: String, $last: Int = 50) {\n  findConversations(\n    input: {filter: {contexts: $contexts, includedLabels: [], excludedLabels: [], metadata: [], scope: $scope}}\n    last: $last\n    before: $before\n    after: $after\n  ) {\n    pageInfo {\n      ...FindConversationsPageInfoFragment\n      __typename\n    }\n    conversations {\n      ...ConversationSnippetFragment\n      __typename\n    }\n    __typename\n  }\n}\n\nfragment FindConversationsPageInfoFragment on PageInfo {\n  endCursor\n  hasNextPage\n  hasPreviousPage\n  startCursor\n  __typename\n}\n\nfragment ConversationSnippetFragment on Conversation {\n  id\n  context\n  lastEvent {\n    type\n    publicationDateTime\n    messageBody\n    messagePreview\n    id\n    attachments {\n      fileName\n      __typename\n    }\n    author {\n      accountKey\n      __typename\n    }\n    __typename\n  }\n  locks {\n    primary\n    reason\n    timestamp\n    __typename\n  }\n  userReadsInfo {\n    unreadCount\n    __typename\n  }\n  participants {\n    accountKey\n    externalParticipantId\n    participantName\n    role\n    __typename\n  }\n  scope {\n    key\n    value\n    __typename\n  }\n  userLabelInfo {\n    labels\n    __typename\n  }\n  __typename\n}\n',
    variables: {
      last: take,
      contexts: ['RESUME_CONTACT'],
    },
    contexts: ['RESUME_CONTACT'],
    last: take,
  };
};

// Minimal discovery payload — used as `body` in IndeedArgs.
// Only fetches id + unreadCount to check if new messages exist.
export const findConversationsByContextDiscoveryPayload = {
  operationName: 'FindConversationsByContext',
  extensions: {},
  query:
    'query FindConversationsByContext($contexts: [ConversationContext!], $last: Int = 50) {\n  findConversations(\n    input: {filter: {contexts: $contexts}}\n    last: $last\n  ) {\n    conversations {\n      id\n      scope {\n        key\n        value\n        __typename\n      }\n      __typename\n    }\n    __typename\n  }\n}\n',
  variables: {
    last: 1,
    contexts: ['RESUME_CONTACT'],
  },
};

// GetConversations payload — used as `getConversationsBody` in IndeedArgs.
// {{ADVERTISER_KEY}} is replaced by the extension at poll time.
export const getConversationsPayload = {
  operationName: 'GetConversations',
  extensions: {},
  query:
    'query GetConversations($last: Int = 10, $before: String, $first: Int, $after: String, $contexts: [ConversationContext!], $scope: [ConversationScopeInput!]) {\n  findConversations(\n    input: {filter: {contexts: $contexts, scope: $scope}}\n    last: $last\n    before: $before\n    first: $first\n    after: $after\n  ) {\n    edges {\n      node {\n        ...ConversationPreview\n        __typename\n      }\n      cursor\n      __typename\n    }\n    pageInfo {\n      startCursor\n      endCursor\n      hasNextPage\n      hasPreviousPage\n      __typename\n    }\n    __typename\n  }\n}\n\nfragment ConversationPreview on Conversation {\n  id\n  __typename\n  creationDateTime\n  context\n  scope {\n    key\n    value\n    __typename\n  }\n  lastEvent {\n    author {\n      accountKey\n      role\n      __typename\n    }\n    type\n    subType\n    messageContentFormat\n    messagePreview\n    publicationDateTime\n    __typename\n  }\n  participants {\n    accountKey\n    participantName\n    removed\n    role\n    accessLevel\n    __typename\n  }\n  userReadsInfo {\n    lastVisited\n    readCursor\n    unreadCount\n    __typename\n  }\n  userLabelInfo {\n    labels\n    __typename\n  }\n  __typename\n}\n',
  variables: {
    last: 50,
    contexts: ['RESUME_CONTACT'],
    scope: [{ key: 'ADVERTISER_KEY', value: '{{ADVERTISER_KEY}}' }],
  },
};

// GetConversationAndEvents payload — used as `getConversationEventsBody` in IndeedArgs.
// {{CONVERSATION_ID}} is replaced by the extension per-conversation.
export const getConversationEventsPayloadTemplate = {
  operationName: 'GetConversationAndEvents',
  extensions: {},
  query:
    'query GetConversationAndEvents($conversationId: ID!) {\n  conversation(input: {conversationId: $conversationId}) {\n    eventsConnection {\n      edges {\n        node {\n          id\n          type\n          subType\n          messageBody\n          messagePreview\n          messageContentFormat\n          publicationDateTime\n          attachments {\n            fileName\n            __typename\n          }\n          author {\n            accountKey\n            role\n            __typename\n          }\n          __typename\n        }\n        __typename\n      }\n      __typename\n    }\n    __typename\n  }\n}\n',
  variables: {
    conversationId: '{{CONVERSATION_ID}}',
  },
};

// ContactSummary payload — used as `contactSummaryBody` in IndeedArgs.
// {{CANDIDATE_ID}} is replaced by the extension per-candidate.
export const contactSummaryPayloadTemplate = {
  operationName: 'ContactSummary',
  extensions: {},
  query:
    'query ContactSummary($input: TalentEngagementSummaryInput!) {\n  contactRecord: talentEngagementSummary(input: $input) {\n    candidate {\n      id\n      email\n      phoneNumber\n      __typename\n    }\n    __typename\n  }\n}\n',
  variables: {
    input: {
      candidateId: '{{CANDIDATE_ID}}',
    },
  },
};

export const getIndeedJobsPayload = (countryCode: CountryCode = 'NL') => {
  return {
    operationName: 'EJSM_GetJobsList',
    variables: {
      input: {
        filter: {
          location: {
            match: countryCode,
            countryCode: countryCode,
          },
          allOf: [
            {
              anyOf: [
                { not: { hostedJobStatus: ['CLOSED', 'PAUSED'] } },
                { hostedJobStatus: ['CLOSED'] },
                { hostedJobStatus: ['PAUSED'] },
              ],
            },
          ],
        },
        sort: [
          {
            sortField: 'datePostedOnIndeed',
            sortDirection: 'DESC',
          },
        ],
        // Tech debt: This is a temporary fix to get all jobs. We should use the pagination instead.
        limit: 1000,
      },
      hasHJPReadPermissions: true,
      inEJSAdditionalRumContext: true,
    },
    extensions: {},
    query:
      'query EJSM_GetJobsList($input: FindEmployerJobsInput!, $hasHJPReadPermissions: Boolean!, $inEJSAdditionalRumContext: Boolean!) {\n  findEmployerJobs(input: $input) {\n    estimatedTotalResultsCount\n    pageInfo {\n      endCursor\n      hasNextPage\n      hasPreviousPage\n      startCursor\n      __typename\n    }\n    ...EJS_Metadata_Search_Fields @include(if: $inEJSAdditionalRumContext)\n    results {\n      employerJob {\n        ...EJSM_SimpleJobPicker_EmployerJob\n        jobData {\n          id\n          ...HJP_Fields_That_Require_Read_Permissions @include(if: $hasHJPReadPermissions)\n          __typename\n        }\n        __typename\n      }\n      __typename\n    }\n    __typename\n  }\n}\n\nfragment EJS_Metadata_Search_Fields on FindEmployerJobsPayload {\n  metadata {\n    searchRequestTk\n    __typename\n  }\n  __typename\n}\n\nfragment EJSM_SimpleJobPicker_EmployerJob on EmployerJob {\n  id\n  jobData {\n    id\n    __typename\n    title\n    description\n    formattedDescription {\n          htmlDescription\n          __typename\n        }\n    dateCreated\n    datePostedOnIndeed\n    externalRequisitionId\n    advertisingLocations {\n      location\n      active\n      __typename\n    }\n    ... on HostedJobPost {\n      status\n      legacyId\n      applicationsCount {\n        milestoneCounts {\n          count\n          milestone\n          __typename\n        }\n        __typename\n      }\n      __typename\n    }\n    ... on ExternalJobPost {\n      applyMethod {\n        ... on JobPostEmailApplyMethod {\n          emails\n          __typename\n        }\n        method\n        __typename\n      }\n      claimData {\n        status\n        isEditable\n        __typename\n      }\n      __typename\n    }\n  }\n  __typename\n}\n\nfragment HJP_Fields_That_Require_Read_Permissions on HostedJobPost {\n  applyMethod {\n    ... on JobPostEmailApplyMethod {\n      emails\n      __typename\n    }\n    method\n    __typename\n  }\n  __typename\n}\n',
  };
};

export const getContactSummaryPayload = (candidateId: string) => ({
  operationName: 'ContactSummary',
  query:
    'query ContactSummary($input: TalentEngagementSummaryInput!) {\n  contactRecord: talentEngagementSummary(input: $input) {\n    permissions {\n      action\n      status\n      __typename\n    }\n    candidate {\n      id\n      email\n      phoneNumber\n      __typename\n    }\n    contactHistory: engagements {\n      contactType: type\n      date: created\n      dateStamp: created\n      email: senderEmail\n      contactedBy: senderId\n      contactOutcome: status\n      statusHistory {\n        status\n        date: modifiedDate\n        dateStamp: modifiedDate\n        __typename\n      }\n      __typename\n    }\n    __typename\n  }\n}\n',
  variables: {
    input: {
      candidateId: candidateId,
    },
  },
});
